/**
 * RightPanel (shell) — the post-Cloudscape, Tailwind-only project-context
 * surface that mounts in the AppShell's right column for routes that opt
 * into it.
 *
 * Four sections, top to bottom:
 *   1. Header                — "PROJECT CONTEXT" eyebrow + optional close
 *   2. Active agents         — live phase pills, run-shape derivation
 *   3. Live context layers   — read-only display of which knowledge sources
 *                              the AI is currently drawing from.
 *   4. Connections           — service health pills (Code Browser, Taskei, etc.)
 *
 * All sections are collapsible; collapse state is persisted per-employee via
 * `useRightPanelStore`.
 *
 * Server state (`useChatStream`) drives the agents list AND the active
 * context layers — the AI decides which layers it pulls per turn; the user
 * does not toggle them. Users can mention a layer in their prompt
 * ("check the wiki for…") to nudge the AI; the panel is informational only.
 */
import { useEffect, useMemo, type ReactElement } from 'react';
import { ArrowRight, ChevronDown, ChevronLeft, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useChatStream, type GraphEdge } from '@/stores/chatStream.store';
import { useAgentActivity } from '@/stores/agentActivity.store';
import {
  useRightPanelStore,
  type RightPanelSection,
} from '@/stores/rightPanel.store';
import { AGENTS, type AgentId } from '@/tokens/colors';
import { wayneFetch } from '@/api/wayneFetch';
import { useStreamingAgentIds } from '@/hooks/useStreamingAgentIds';
import AgentColorChip from '@/components/atoms/AgentColorChip';
import StatusBar from './StatusBar';
import MemoryPanelView from '@/components/organisms/MemoryPanelView';
import ObservationsStrip from '@/components/molecules/ObservationsStrip';

interface ContextLayer {
  readonly id: string;
  readonly name: string;
  readonly description: string;
}

const PROJECT_LAYERS: readonly ContextLayer[] = [
  {
    id: 'wiki',
    name: 'Wiki pages',
    description: 'Internal team space + the pages your bindles own.',
  },
  {
    id: 'oncall',
    name: 'Oncall schedule',
    description: 'Active pages + the human currently primary.',
  },
  {
    id: 'code',
    name: 'Code Browser',
    description: 'Mainline + recent CRs across your owned packages.',
  },
  {
    id: 'taskei',
    name: 'Taskei rooms',
    description: 'Sprint state + open tickets in rooms you watch.',
  },
  {
    id: 'quip',
    name: 'Quip docs',
    description: 'Shared design folders you can read.',
  },
  {
    id: 'sage',
    name: 'Sage knowledge base',
    description: 'Indexed wiki + design knowledge.',
  },
];

type RunShape = 'idle' | 'single' | 'parallel' | 'sequential' | 'hierarchical';

const RUN_SHAPE_LABEL: Record<RunShape, string> = {
  idle: 'Idle',
  single: 'Single agent',
  parallel: 'Parallel',
  sequential: 'Sequential',
  hierarchical: 'Hierarchical',
};

interface ActiveAgentRow {
  readonly agentId: AgentId;
  readonly phase: string;
  readonly currentTool: string | null;
}

function deriveActive(
  agents: ReturnType<typeof useChatStream.getState>['agents'],
): readonly ActiveAgentRow[] {
  return (
    Object.values(agents) as ReadonlyArray<
      | {
          readonly agentId: AgentId;
          readonly phase: string;
          readonly currentTool: string | null;
        }
      | undefined
    >
  )
    .filter((a): a is ActiveAgentRow => Boolean(a))
    .filter((a) => a.phase !== 'idle');
}

function deriveRunShape(active: readonly ActiveAgentRow[], edgeCount: number): RunShape {
  if (active.length === 0) return 'idle';
  if (active.length === 1) return 'single';
  if (edgeCount > active.length) return 'hierarchical';
  if (edgeCount === 0) return 'parallel';
  return 'sequential';
}

/** Filter edges to only include those between known agents in the AGENTS registry. */
function filterValidEdges(edges: readonly GraphEdge[]): readonly GraphEdge[] {
  return edges.filter(
    (e) =>
      AGENTS[e.from as keyof typeof AGENTS] != null &&
      AGENTS[e.to as keyof typeof AGENTS] != null,
  );
}

const EDGE_KIND_LABEL: Record<string, string> = {
  handoff: 'handoff',
  message: 'msg',
  observation: 'obs',
};

/**
 * Read the live set of context layers the AI is currently drawing from.
 *
 * For now this is derived from `useChatStream`'s recent tool calls — any
 * agent that called a `wiki.*` / `taskei.*` / `oncall.*` tool in the
 * current session marks the corresponding layer as active. When that
 * heuristic isn't enough (e.g., the AI is reading a layer without
 * surfacing a tool call), the backend can enrich the chatStream payload
 * with an explicit `layersInUse` set; this hook will pick it up
 * transparently because it reads the same store.
 */
function useActiveLayerIds(): ReadonlySet<string> {
  const agents = useChatStream((s) => s.agents);
  return useMemo(() => {
    const active = new Set<string>();
    for (const a of Object.values(agents)) {
      if (!a) continue;
      const tool = a.currentTool ?? '';
      if (!tool) continue;
      // Map well-known tool prefixes onto context layer IDs.
      if (tool.startsWith('wiki.') || tool.startsWith('w.amazon.')) active.add('wiki');
      if (tool.startsWith('oncall.') || tool.startsWith('whoisoncall.')) active.add('oncall');
      if (tool.startsWith('code.') || tool.startsWith('cr.') || tool.startsWith('git.'))
        active.add('code');
      if (tool.startsWith('taskei.') || tool.startsWith('jira.') || tool.startsWith('sim.'))
        active.add('taskei');
      if (tool.startsWith('quip.')) active.add('quip');
      if (tool.startsWith('sage.')) active.add('sage');
    }
    return active;
  }, [agents]);
}

// -- Connections section data -------------------------------------------------

/**
 * The 6 services displayed as connection pills in the right panel.
 * If the backend returns fewer/more, we only show these 6 in the
 * panel (the full page is at /connections).
 */
const PANEL_SERVICES = [
  'Code Browser',
  'Taskei',
  'Wiki',
  'Oncall',
  'Sage',
  'Phonetool',
] as const;

interface ConnectionPill {
  readonly service: string;
  readonly connected: boolean;
}

interface ConnectionsApiRow {
  readonly id: string;
  readonly service: string;
  readonly health: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
}

function useConnectionPills(): {
  pills: readonly ConnectionPill[];
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery<readonly ConnectionsApiRow[]>({
    queryKey: ['connections', 'panel'],
    queryFn: async () => {
      const raw = await wayneFetch<readonly ConnectionsApiRow[] | { rows: readonly ConnectionsApiRow[] }>('/connections');
      if (Array.isArray(raw)) return raw;
      if (raw && typeof raw === 'object' && 'rows' in raw && Array.isArray(raw.rows)) return raw.rows;
      return [];
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const pills: readonly ConnectionPill[] = useMemo(() => {
    const healthMap = new Map<string, boolean>();
    if (data && Array.isArray(data)) {
      for (const row of data) {
        healthMap.set(row.service, row.health === 'healthy');
      }
    }
    return PANEL_SERVICES.map((service) => ({
      service,
      connected: healthMap.get(service) ?? false,
    }));
  }, [data]);

  return { pills, isLoading };
}

// -- Collapsible section header -----------------------------------------------

interface SectionHeaderProps {
  readonly label: string;
  readonly count?: number;
  readonly collapsed: boolean;
  readonly onToggle: () => void;
  readonly id: string;
}

function SectionHeader({
  label,
  count,
  collapsed,
  onToggle,
  id,
}: SectionHeaderProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      className="flex items-center justify-between w-full text-left group"
      id={id}
    >
      <span
        className="text-wayne-ink-3 font-medium uppercase"
        style={{ fontSize: 10, letterSpacing: '0.08em' }}
      >
        {label}
        {count !== undefined ? ` · ${count}` : ''}
      </span>
      <ChevronDown
        size={14}
        className={[
          'text-wayne-ink-3 transition-transform duration-200',
          collapsed ? '-rotate-90' : '',
        ].join(' ')}
      />
    </button>
  );
}

// -- Main component -----------------------------------------------------------

export interface RightPanelProps {
  /**
   * If supplied, a small close button appears in the top-right corner. The
   * panel is otherwise self-contained — when no `onClose` is wired the
   * close affordance is hidden.
   */
  readonly onClose?: () => void;
}

export default function RightPanel(props: RightPanelProps = {}): ReactElement {
  const { onClose } = props;
  const { user } = useAuth();
  const agents = useChatStream((s) => s.agents);
  const edges = useChatStream((s) => s.edges);
  const activeLayerIds = useActiveLayerIds();
  const restActiveAgents = useAgentActivity((s) => s.activeAgents);

  const collapsedSections = useRightPanelStore((s) => s.collapsedSections);
  const toggleSection = useRightPanelStore((s) => s.toggleSection);
  const hydrateFromStorage = useRightPanelStore((s) => s.hydrateFromStorage);
  const activeView = useRightPanelStore((s) => s.activeView);
  const setActiveView = useRightPanelStore((s) => s.setActiveView);

  const streamingIds = useStreamingAgentIds();

  const { pills: connectionPills, isLoading: connectionsLoading } =
    useConnectionPills();

  // Hydrate persisted collapse state on mount.
  useEffect(() => {
    const employeeId = user?.employeeId ?? 'anonymous';
    hydrateFromStorage(employeeId, 'default');
  }, [user?.employeeId, hydrateFromStorage]);

  const wsActive = useMemo(() => deriveActive(agents), [agents]);

  // Merge WS and REST sources: if WS already shows activity, use it;
  // otherwise fall back to REST-based recent activity for visual feedback.
  const active: readonly ActiveAgentRow[] = useMemo(() => {
    if (wsActive.length > 0) return wsActive;
    // WS is all idle — synthesize rows from REST-activity entries.
    const restRows: ActiveAgentRow[] = [];
    for (const entry of Object.values(restActiveAgents)) {
      if (entry) {
        restRows.push({
          agentId: entry.agentId,
          phase: 'thinking',
          currentTool: null,
        });
      }
    }
    return restRows;
  }, [wsActive, restActiveAgents]);

  const validEdges = useMemo(() => filterValidEdges(edges), [edges]);

  const runShape = useMemo(
    () => deriveRunShape(active, validEdges.length),
    [active, validEdges.length],
  );

  const isCollapsed = (section: RightPanelSection): boolean =>
    collapsedSections.has(section);

  // -- Memory view ---------------------------------------------------------------
  if (activeView === 'memory') {
    return (
      <div
        className="flex flex-col gap-6 h-full min-h-0"
        style={{ transition: 'background-color 400ms var(--ease-default), color 300ms var(--ease-default)' }}
      >
        {/* Back button */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveView('context')}
            className="inline-flex items-center gap-1 text-wayne-ink-3 hover:text-wayne-ink transition-colors text-[11px] font-medium uppercase"
            style={{ letterSpacing: '0.08em' }}
            aria-label="Back to project context"
          >
            <ChevronLeft size={12} />
            Context
          </button>
        </div>

        {/* Memory panel content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <MemoryPanelView />
        </div>

        {/* System health status bar */}
        <StatusBar />
      </div>
    );
  }

  // -- Context view (default) ---------------------------------------------------
  return (
    <div
      className="flex flex-col gap-6 h-full min-h-0"
      style={{ transition: 'background-color 400ms var(--ease-default), color 300ms var(--ease-default)' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div
          className="text-wayne-ink-3 font-medium uppercase"
          style={{ fontSize: 11, letterSpacing: '0.08em', marginBottom: 12 }}
        >
          PROJECT CONTEXT
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close project context"
            className="text-wayne-ink-3 hover:text-wayne-ink transition-colors -mt-1"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>

      {/* Active agents */}
      <section aria-labelledby="right-panel-active-agents">
        <SectionHeader
          label="ACTIVE AGENTS"
          count={active.length}
          collapsed={isCollapsed('activeAgents')}
          onToggle={() => toggleSection('activeAgents')}
          id="right-panel-active-agents"
        />
        <div
          className={[
            'overflow-hidden transition-all duration-200',
            isCollapsed('activeAgents')
              ? 'max-h-0 opacity-0'
              : 'max-h-[2000px] opacity-100',
          ].join(' ')}
        >
          <div className="mt-3">
            {active.length === 0 ? (
              <div
                className="text-wayne-ink-3 italic"
                style={{ fontSize: 13 }}
                data-testid="right-panel-empty-agents"
              >
                No active agents. Send a message to dispatch.
              </div>
            ) : (
              <ul className="flex flex-col gap-0">
                {active.map((agent) => {
                  const identity = AGENTS[agent.agentId as keyof typeof AGENTS];
                  if (!identity) return null;
                  const colorVar = `var(--agent-${agent.agentId})`;
                  const subline = agent.currentTool ?? 'click to dispatch';
                  return (
                    <li key={agent.agentId}>
                      <div
                        data-testid={`right-panel-agent-${agent.agentId}`}
                        className="bg-wayne-surface border border-wayne-hairline rounded-lg p-3 mb-2 flex items-center gap-3"
                        style={{ transition: 'background-color 300ms var(--ease-default), border-color 300ms var(--ease-default)' }}
                      >
                        <span
                          aria-hidden="true"
                          className="rounded-full shrink-0"
                          style={{
                            width: 6,
                            height: 6,
                            backgroundColor: colorVar,
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div
                            className="text-wayne-ink font-medium truncate"
                            style={{ fontSize: 13 }}
                          >
                            {identity.displayName}
                          </div>
                          <div
                            className="text-wayne-ink-3 font-mono truncate"
                            style={{ fontSize: 12 }}
                          >
                            {subline}
                          </div>
                        </div>
                        <span
                          className="text-wayne-ink-3 border border-wayne-hairline rounded-full"
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            transition: 'border-color 300ms var(--ease-default), color 300ms var(--ease-default)',
                          }}
                        >
                          {agent.phase}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {/* Agent-to-agent edges */}
            {validEdges.length > 0 && (
              <div
                className="mt-2 flex flex-col gap-1"
                data-testid="right-panel-edges"
              >
                {validEdges.map((edge, idx) => {
                  const fromAgent = AGENTS[edge.from as keyof typeof AGENTS];
                  const toAgent = AGENTS[edge.to as keyof typeof AGENTS];
                  if (!fromAgent || !toAgent) return null;
                  return (
                    <div
                      key={`${edge.from}-${edge.to}-${edge.ts}-${idx}`}
                      className="flex items-center gap-1.5 text-wayne-ink-3"
                      style={{ fontSize: 11 }}
                    >
                      <span className="text-wayne-ink-2 font-medium">
                        {fromAgent.displayName}
                      </span>
                      <ArrowRight size={10} className="text-wayne-ink-3 shrink-0" />
                      <span className="text-wayne-ink-2 font-medium">
                        {toAgent.displayName}
                      </span>
                      <span
                        className="text-wayne-ink-3 border border-wayne-hairline rounded px-1"
                        style={{ fontSize: 10 }}
                      >
                        {EDGE_KIND_LABEL[edge.kind] ?? edge.kind}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Run shape indicator */}
            <div
              className="text-wayne-ink-3 mt-2"
              style={{ fontSize: 12 }}
              data-testid="right-panel-run-shape"
            >
              Run shape:{' '}
              <strong className="text-wayne-ink">{RUN_SHAPE_LABEL[runShape]}</strong>
            </div>
            {/* Per-agent streaming mini-status rows */}
            {streamingIds.length > 0 && (
              <div className="mt-2 flex flex-col gap-1">
                {streamingIds.map((id) => (
                  <div
                    key={id}
                    data-testid={`right-panel-streaming-${id}`}
                    className="text-xs text-wayne-ink-3 flex items-center gap-2"
                  >
                    <AgentColorChip agent={id as AgentId} size={8} />
                    <span>{AGENTS[id as AgentId]?.displayName ?? id} responding…</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Live agent observations — scratchpad stream from agents */}
      <ObservationsStrip />

      {/* Live context layers — read-only status. The AI decides which to use;
          the user can mention one in their prompt to nudge the AI. */}
      <section aria-labelledby="right-panel-context-layers">
        <SectionHeader
          label="LIVE CONTEXT LAYERS"
          collapsed={isCollapsed('liveContextLayers')}
          onToggle={() => toggleSection('liveContextLayers')}
          id="right-panel-context-layers"
        />
        <div
          className={[
            'overflow-hidden transition-all duration-200',
            isCollapsed('liveContextLayers')
              ? 'max-h-0 opacity-0'
              : 'max-h-[2000px] opacity-100',
          ].join(' ')}
        >
          <div className="mt-3">
            <ul className="flex flex-col">
              {PROJECT_LAYERS.map((layer) => {
                const isActive = activeLayerIds.has(layer.id);
                return (
                  <li
                    key={layer.id}
                    className="flex items-start gap-3 py-2"
                    data-testid={`right-panel-layer-${layer.id}`}
                    data-active={isActive ? 'true' : 'false'}
                  >
                    <span
                      aria-hidden="true"
                      className="rounded-full shrink-0 mt-1.5"
                      style={{
                        width: 6,
                        height: 6,
                        backgroundColor: isActive
                          ? 'var(--wayne-accent)'
                          : 'var(--wayne-hairline-2)',
                        boxShadow: isActive
                          ? '0 0 0 3px color-mix(in srgb, var(--wayne-accent) 18%, transparent)'
                          : 'none',
                        transition: 'background-color 200ms ease, box-shadow 200ms ease',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div
                        className={
                          isActive
                            ? 'text-wayne-ink font-medium'
                            : 'text-wayne-ink-2 font-medium'
                        }
                        style={{ fontSize: 13 }}
                      >
                        {layer.name}
                        {isActive ? (
                          <span
                            className="ml-2 text-wayne-accent font-mono uppercase"
                            style={{ fontSize: 10, letterSpacing: '0.08em' }}
                            aria-label="Currently in use"
                          >
                            in use
                          </span>
                        ) : null}
                      </div>
                      <div
                        className="text-wayne-ink-3"
                        style={{ fontSize: 12 }}
                      >
                        {layer.description}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <p
              className="text-wayne-ink-3 mt-3"
              style={{ fontSize: 11, lineHeight: 1.5 }}
            >
              Wayne picks layers automatically. Mention one in your prompt to nudge it.
            </p>
          </div>
        </div>
      </section>

      {/* Connections — service health pills */}
      <section aria-labelledby="right-panel-connections">
        <SectionHeader
          label="CONNECTIONS"
          count={connectionPills.filter((p) => p.connected).length}
          collapsed={isCollapsed('connections')}
          onToggle={() => toggleSection('connections')}
          id="right-panel-connections"
        />
        <div
          className={[
            'overflow-hidden transition-all duration-200',
            isCollapsed('connections')
              ? 'max-h-0 opacity-0'
              : 'max-h-[2000px] opacity-100',
          ].join(' ')}
        >
          <div className="mt-3">
            {connectionsLoading ? (
              <div
                className="text-wayne-ink-3 italic"
                style={{ fontSize: 12 }}
                data-testid="right-panel-connections-loading"
              >
                Loading connections...
              </div>
            ) : (
              <div
                className="flex flex-wrap gap-2"
                data-testid="right-panel-connections-grid"
              >
                {connectionPills.map((pill) => (
                  <span
                    key={pill.service}
                    className="inline-flex items-center gap-1.5 border border-wayne-hairline rounded-full px-2.5 py-1"
                    style={{ fontSize: 12, transition: 'background-color 300ms var(--ease-default), border-color 300ms var(--ease-default), color 300ms var(--ease-default)' }}
                    data-testid={`right-panel-connection-${pill.service.toLowerCase().replace(/\s+/g, '-')}`}
                    data-connected={pill.connected ? 'true' : 'false'}
                  >
                    <span
                      aria-hidden="true"
                      className="rounded-full shrink-0"
                      style={{
                        width: 6,
                        height: 6,
                        backgroundColor: pill.connected
                          ? 'var(--wayne-green)'
                          : 'var(--wayne-ink-3)',
                        transition: 'background-color 200ms ease',
                      }}
                    />
                    <span className="text-wayne-ink-2">{pill.service}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* System health status bar */}
      <StatusBar />
    </div>
  );
}
